import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AssetDto, AssetVersionDto, GalleryDto } from '@nusign/picflow-backend-types';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { GALLERY_DOWNLOAD_SIZE, galleryDownloadSizePreferenceList } from '../consts';
import { downloadAsset } from '../gallery/lib/download-asset';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { convertGalleryDownloadSize } from '../lib/convert-gallery-download-size';
import { estimateResizedAssetSize } from '../lib/estimate-resized-asset-size';
import { sleep } from '../lib/sleep';
import { IGNORE_ALL_ERRORS, IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { GalleryPasswordService } from './gallery-password.service';
import { SentryService } from './sentry.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';
import { VirusScanService } from './virus-scan.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadAssetService {
  private toastService = inject(ToastService);
  private config = inject(PICFLOW_CONFIG);
  private storage = inject(StorageService);
  private http = inject(HttpClient);
  private galleryPasswordService = inject(GalleryPasswordService);
  private virusScanService = inject(VirusScanService);
  private sentryService = inject(SentryService);
  private state = inject(StateService);

  public autoDownloadAsset(
    asset: AssetDto,
    version: AssetVersionDto,
    gallery: GalleryDto,
    longEdge: number | 'auto',
    reportDownloads = true
  ) {
    const enabledSizes = convertGalleryDownloadSize(gallery.download_media_size);
    if (longEdge === 'auto')
      longEdge = GALLERY_DOWNLOAD_SIZE[galleryDownloadSizePreferenceList.find(size => enabledSizes[size])];

    return new Observable<'downloading' | 'downloaded' | boolean>(observer => {
      (async () => {
        // const isIos = IsIos();
        // const isMobile = ScreenSizes.screenSize() === 'mobile';
        // if (isIos && navigator.share && isMobile) {
        //   observer.next('downloading');
        //   try {
        //     const shareData = await this.getShareData(asset, version, longEdge as number);
        //     observer.next('downloaded');
        //     if (shareData && navigator.canShare?.(shareData)) {
        //       const shared = await this.shareAssetVersion(asset, version, gallery.id, shareData, reportDownloads);
        //       observer.next(shared);
        //       observer.complete();
        //       return;
        //     }
        //   } catch {}
        // }

        observer.next('downloading');
        await this.downloadAssetVersion(asset, asset.version_data, gallery.id, longEdge as number, reportDownloads);
        observer.next(true);
        observer.complete();
      })();
    });
  }

  // private async getShareData(asset: AssetDto, version: AssetVersionDto, longEdge?: number) {
  //   const url = getDownloadUrl(asset, version, longEdge, this.config.assetsEndpoint, asset.watermark_enabled);
  //   const extension = (url.split(/[?#]/)[0].split('.').pop() || '').toLowerCase();

  //   const filename = `${asset.name}.${extension}`;

  //   const res = await fetch(`${url}?download=${filename}`);
  //   if (!res.ok) {
  //     throw new Error(`Failed to fetch asset for sharing: ${res.statusText}`);
  //   }
  //   const blob = await res.blob();

  //   const file = new File([blob], filename, { type: blob.type });

  //   return {
  //     title: asset.name,
  //     text: `Download ${asset.name}`,
  //     files: [file],
  //   };
  // }

  // private async shareAssetVersion(
  //   asset: AssetDto,
  //   version: AssetVersionDto,
  //   galleryId: string,
  //   shareData: ShareData,
  //   reportDownloads = true
  // ) {
  //   if (!(await this.downloadCheck(version))) return;

  //   try {
  //     await navigator.share(shareData);
  //     if (reportDownloads) {
  //       this.reportSingleAssetDownload(asset.id, galleryId, version.id);
  //     }
  //     return true;
  //   } catch (err) {
  //     if (err?.name === 'AbortError') return false;
  //     console.warn('Error sharing asset version:', err);
  //     throw err;
  //   }
  // }

  private async downloadCheck(version: AssetVersionDto) {
    if (this.state.galleryPreviewMode) {
      this.toastService.add('Downloads are not available in preview mode', 'error');
      return false;
    }

    try {
      await firstValueFrom(this.virusScanService.onScanned(version));
    } catch (err) {
      console.warn(err);
      this.toastService.add('Download Failed, virus detected', 'error');
      return false;
    }

    return true;
  }

  private async downloadAssetVersion(
    asset: AssetDto,
    version: AssetVersionDto,
    galleryId: string,
    longEdge?: number,
    reportDownloads = true
  ) {
    if (!(await this.downloadCheck(version))) return;
    try {
      await Promise.all([
        downloadAsset(asset, version, longEdge, this.config.assetsEndpoint, asset.watermark_enabled),
        sleep(250),
      ]);
    } catch (err) {
      console.warn(err);
      this.sentryService.captureException('asset-download-failed');
      this.toastService.add('Download Failed. Please try again', 'error');
    }
    if (reportDownloads) {
      this.reportSingleAssetDownload(asset.id, galleryId, version.id);
    } else {
      this.reportSingleAssetDownloadInternal(
        asset.id,
        galleryId,
        asset.tenant,
        longEdge,
        estimateResizedAssetSize(version, longEdge)
      );
    }
  }

  public async reportSingleAssetDownload(assetId: string, galleryId: string, versionId: string) {
    const assetsDownloaded = this.storage.get<string[]>('downloaded_assets') || [];
    const assetDownloaded = assetsDownloaded.includes(assetId);
    if (assetDownloaded) return; // Only log one download per session
    this.storage.set('downloaded_assets', [...assetsDownloaded, assetId], true);
    try {
      await lastValueFrom(
        this.http.post(
          addGalleryPassword(
            `${this.config.apiEndpoint}/assets/${assetId}/downloads`,
            this.galleryPasswordService.getPassword(galleryId)
          ),
          { asset_version: versionId },
          {
            context: new HttpContext().set(IGNORE_AUTH_ERRORS, true),
          }
        )
      );
    } catch {
      console.error('Ignoring assetId download reporting error');
    }
  }

  private async reportSingleAssetDownloadInternal(
    assetId: string,
    galleryId: string,
    tenantId: string,
    longEdge: number,
    size: bigint
  ) {
    const assetsDownloaded = this.storage.get<string[]>('downloaded_assets') || [];
    const assetDownloaded = assetsDownloaded.includes(assetId);
    if (assetDownloaded) return; // Only log one download per session
    this.storage.set('downloaded_assets', [...assetsDownloaded, assetId], true);
    try {
      await lastValueFrom(
        this.http.post(
          `${this.config.correctPicflowUrl.replace('/embed', '')}/internal-api/report-embed-download`,
          {
            asset_id: assetId,
            gallery_id: galleryId,
            long_edge: longEdge,
            size: Number(size),
            url: window.location.href,
            tenant_id: tenantId,
          },
          {
            context: new HttpContext().set(IGNORE_ALL_ERRORS, true),
            responseType: 'text',
            withCredentials: false,
          }
        )
      );
    } catch {
      console.error('Ignoring assetId download reporting error');
    }
  }
}
